import {
  type Unsubscribe,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from "firebase/auth"
import type { ReactNode } from "react"
import React from "react"

import type { Authentication } from "@/lib/authentication"
import { AuthenticationContext } from "@/lib/authentication/context"
import { fetchPatient } from "@/lib/authentication/fetchPatient"
import { getAuthenticationProviderId } from "@/lib/authentication/getProviderId"
import { isAuthError } from "@/lib/authentication/isAuthError"
import { isFirebaseUserCredential } from "@/lib/authentication/isFirebaseUserCredential"
import { buildProviderAuthenticationResult } from "@/lib/authentication/providerResult"
import { updatePatientOnServer } from "@/lib/authentication/updatePatientOnServer"
import type { Loading } from "@/lib/loading"
import { ProcessLocking } from "@/lib/process_locking"
import { ReactPerformance } from "@/lib/react_performance"

import { auth } from "@/config/firebase"

type Props = {
  children: ReactNode
  loading: Loading.ContextData
}

type State = {
  authUser: Authentication.User | void
  patient: Authentication.Patient | void
  processing: string | void
  update: boolean
}

export class AuthenticationContainerComponent extends React.Component<
  Props,
  State
> {
  public state: State = {
    authUser: auth.currentUser || undefined,
    patient: undefined,
    processing: undefined,
    update: false,
  }

  private listeners: Unsubscribe[] = []

  public componentDidMount = () => {
    this.listeners.push(
      auth.onAuthStateChanged(async (authUser) => {
        if (!authUser) {
          this.updateState(
            {
              authUser: undefined,
              patient: undefined,
            },
            () => {
              return this.onReady()
            }
          )
          return
        }

        if (authUser.uid !== this.state.authUser?.uid) {
          const auth_token = await authUser.getIdToken()

          const patient = await fetchPatient({ auth_token, uid: authUser.uid })

          if (patient) {
            this.updateState(
              {
                authUser,
                patient,
              },
              () => {
                return this.onReady()
              }
            )
            return
          }
        }

        this.updateState(
          {
            authUser,
          },
          () => {
            return this.onReady()
          }
        )
      })
    )
  }

  public componentWillUnmount = () => {
    this.listeners.forEach((unsubscribe) => {
      unsubscribe()
    })
  }

  private emailAndPassword: Authentication.ContextData[`emailAndPassword`] = {
    forgotPassword: async ({ email }) => {
      await sendPasswordResetEmail(auth, email)
    },
    register: (data) => {
      return ProcessLocking.asLockedProcess({
        component: this,
        process: async () => {
          try {
            await createUserWithEmailAndPassword(
              auth,
              data.email,
              data.password
            )
            const registered = await this.updatePatient(data)

            return {
              emailAlreadyInUse: false,
              registered,
            }
          } catch (error) {
            if (
              isAuthError(error) &&
              error.code === `auth/email-already-in-use`
            ) {
              return {
                emailAlreadyInUse: true,
                registered: false,
              }
            }

            throw error
          }
        },
        name: `AuthenticationContainer#emailAndPassword.register`,
      })
    },
    signIn: ({ email, password, persistence }) => {
      return ProcessLocking.asLockedProcess({
        component: this,
        process: async () => {
          await auth.setPersistence(persistence)

          await signInWithEmailAndPassword(auth, email, password)

          return this.verifyAuthenticated()
        },
        name: `AuthenticationContainer#emailAndPassword.signIn`,
      })
    },
  }

  private facebook: Authentication.ContextData[`facebook`] = {
    register: () => {
      return Promise.reject(`Facebook registration is currently disabled.`)
    },
    authenticate: () => {
      return Promise.reject(`Facebook sign in in currently disabled.`)
    },
  }

  private google: Authentication.ContextData[`google`] = {
    authenticate: () => {
      return ProcessLocking.asLockedProcess({
        component: this,
        process:
          async (): Promise<Authentication.ProviderAuthenticationResult> => {
            try {
              this.googleProvider.addScope(`email`)

              const result = await signInWithPopup(auth, this.googleProvider)

              if (isFirebaseUserCredential(result)) {
                return buildProviderAuthenticationResult({
                  authenticated: true,
                  existingPatient: await this.verifyAuthenticated(),
                })
              }

              // Firebase Authentication's types suggest it always returns a UserCredential.
              // The documentation says that it "returns an error" if the sign in fails.
              // We aren't sure if that means that it will always throw one, or if it sometimes
              // returns the error as stated. If it ever returns an error, we want to throw it.
              throw result
            } catch (error) {
              const authError = isAuthError(error) ? error : undefined
              if (authError?.code === `auth/email-already-in-use`) {
                return buildProviderAuthenticationResult({
                  emailAlreadyInUse: true,
                })
              }

              if (authError?.code === `auth/popup-closed-by-user`) {
                return buildProviderAuthenticationResult({
                  cancelled: true,
                })
              }

              throw error
            }
          },
        name: `AuthenticationContainer#google.authenticate`,
      })
    },
    register: (data) =>
      // This function must be called after someone successfully authenticates
      // with Google and then submits the SignUpForm with their patient details.
      {
        return ProcessLocking.asLockedProcess({
          component: this,
          process: async () => {
            const registered = await this.updatePatient(data)
            return {
              emailAlreadyInUse: false,
              registered,
            }
          },
          name: `AuthenticationContainer#google.register`,
        })
      },
  }

  private googleProvider = new GoogleAuthProvider()

  public render = (): ReactNode => {
    return (
      <AuthenticationContext.Provider
        value={{
          authUser: this.state.authUser,
          emailAndPassword: this.emailAndPassword,
          facebook: this.facebook,
          fetchPatient,
          google: this.google,
          patient: this.state.patient,
          processing: this.state.processing,
          providerId: getAuthenticationProviderId(this.state),
          signOut: this.signOut,
        }}
      >
        {this.props.children}
      </AuthenticationContext.Provider>
    )
  }

  private onReady = () => {
    this.props.loading.update({
      authentication: { loading: false },
    })
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    Props,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps }) => {
      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private signOut: Authentication.Functions.SignOut = async () => {
    await auth.signOut()
  }

  private updateState = ReactPerformance.curryUpdateManagedState(this)

  private updatePatient = async (
    updates: Authentication.UpdateData
  ): Promise<boolean> => {
    if (!auth.currentUser) {
      return false
    }

    await updatePatientOnServer({ updates, user: auth.currentUser })

    await updateProfile(auth.currentUser, {
      displayName: `${updates.firstName} ${updates.lastName}`,
    })

    const email =
      auth.currentUser.email ||
      auth.currentUser.providerData.find((userInfo) => {
        return userInfo.email
      })?.email

    // Profile updated
    this.updateState({
      patient: {
        ...updates,
        ...(email ? { email } : {}),
        uid: auth.currentUser.uid,
      },
    })

    return true
  }

  private verifyAuthenticated: Authentication.Functions.VerifyAuthenticated =
    async () => {
      const auth_token = await auth.currentUser?.getIdToken()
      const uid = auth.currentUser?.uid

      if (auth_token && uid) {
        const patient = await fetchPatient({
          auth_token,
          uid,
        })

        if (patient) {
          return true
        }
      }
      return false
    }
}
